import { useMemo } from "react";

interface ForceExitCheckInput {
  isPreApprovalVoid?: boolean;
  readOnly: boolean;
}

interface ForceExitCheckResult {
  shouldForceExit: boolean;
}

/**
 * Pure predicate for whether a deal should trigger a force-exit modal. Force-
 * exit is triggered when isPreApprovalVoid is true and the app is not in
 * read-only mode (analyst holds the VLA lock). Exported so any click-time
 * recheck (e.g. ReviewPage's Save handler) shares this single implementation
 * instead of hand-copying the predicate.
 * @param {ForceExitCheckInput} input - The input parameters.
 * @returns {boolean} True when force-exit should trigger.
 */
export function computeShouldForceExit({ isPreApprovalVoid, readOnly }: ForceExitCheckInput): boolean {
  if (readOnly) {
    return false;
  }

  return isPreApprovalVoid === true;
}

/**
 * Computes whether the current deal should trigger a force-exit modal.
 * @param {ForceExitCheckInput} input - The input parameters.
 * @returns {ForceExitCheckResult} Object with shouldForceExit flag.
 */
export function useForceExitCheck({
  isPreApprovalVoid,
  readOnly,
}: ForceExitCheckInput): ForceExitCheckResult {
  return useMemo(
    () => ({ shouldForceExit: computeShouldForceExit({ isPreApprovalVoid, readOnly }) }),
    [isPreApprovalVoid, readOnly]
  );
}
